import { ManualCheckIn } from "@/types/check-in";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import Combobox from "@/components/ui/common/combobox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { Controller, FormProvider, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { useGetAllHardwareQuery } from "@/services/hardwareApi";
import { useMemo, useState } from "react";
import { SearchComboBox } from "@/components/ui/globalsearch/search-combo-box";
import { useGetAllMemberQuery } from "@/services/memberApi";
import { useGetCoachesQuery } from "@/services/coachApi";
import { useGetStaffsQuery } from "@/services/staffsApi";
import { useCreateManualCheckinMutation } from "@/services/checkInApis";
import {
  Coach,
  ErrorType,
  staffTypesResponseList,
} from "@/types";
import { Action } from "@radix-ui/react-toast";
import { toast } from "@/components/ui/use-toast";
import { LoadingButton } from "@/components/ui/common/loading-button";
import { MemberTableDatatypes } from "@/types/member";
import AutocompleteCombobox from "@/components/ui/common/autocomplete-combobox";
import { FormProps } from "@/types";

const initialValue = {
  user_id: 0,
  notes: "",
  user_type: undefined,
  hardware_key: "",
  facility_id: 0,
  user_name: "",
};

type UserType =
  | MemberTableDatatypes
  | Coach
  | staffTypesResponseList;

const ManualCheckInForm = ({
  isOpen,
  setOpen,
  action,
  setAction,
  refetch,
  data,
  setData,
}: FormProps<ManualCheckIn>) => {
  const [searchTerm, setSearchTerm] = useState("");
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) ?? 0;
  const [createManualCheckIn] = useCreateManualCheckinMutation();
  const {
    data: hardwareData,
    isLoading: ishardwaredataloading,
    error,
    isError,
  } = useGetAllHardwareQuery({
    org_id: orgId,
    query: "",
  });

  const hardwareOptions = useMemo(() => {
    return Array.isArray(hardwareData?.data)
      ? hardwareData?.data.map((hardware) => {
          return {
            value: hardware.connection_key,
            facility_id: hardware.facility_id,
            label: hardware.name,
          };
        })
      : [];
  }, [hardwareData]);

  const form = useForm<ManualCheckIn>({
    mode: "all",
    defaultValues: initialValue,
  });
  const {
    control,
    watch,
    register,
    handleSubmit,
    clearErrors,
    getValues,
    reset,
    setValue,
    formState: { isSubmitting, errors },
  } = form;
  const watcher = watch();

  // Conditionally fetch data based on user type
  const { data: memberData } = useGetAllMemberQuery(
    {
      org_id: orgId,
      query: searchTerm,
    },
    {
      skip: !watcher.user_type,
    }
  );

  const memberTableData = useMemo(() => {
    return Array.isArray(memberData?.data) ? memberData?.data : [];
  }, [memberData]);

  const { data: coachData } = useGetCoachesQuery(
    {
      org_id: orgId,
      query: searchTerm,
    },
    {
      skip: !watcher.user_type,
    }
  );

  const coachTableData = useMemo(() => {
    return Array.isArray(coachData?.data) ? coachData.data : [];
  }, [coachData]);

  const { data: staffData } = useGetStaffsQuery(
    {
      org_id: orgId,
      query: searchTerm,
    },
    {
      skip: !watcher.user_type,
    }
  );

  const staffTableData = useMemo(() => {
    return Array.isArray(staffData?.data) ? staffData.data : [];
  }, [staffData]);

  // Get current users based on selected user type
  const currentUsers = useMemo(() => {
    switch (watcher.user_type) {
      case "member":
        return memberTableData;
      case "coach":
        return coachTableData;
      case "staff":
        return staffTableData;
      default:
        return [];
    }
  }, [watcher.user_type, memberTableData, coachTableData, staffTableData]);

  const onSubmit = async (payload: ManualCheckIn) => {
    try {
      console.log({ payload, action }, "submit");
      const resp = await createManualCheckIn(payload).unwrap();
      if (resp) {
        toast({
          variant: "success",
          title: "Opened gate successfully",
        });
      }
      handleClose();
    } catch (error) {
      console.error("Error", { error });
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in Submission",
          description: `${typedError.data?.detail || typedError.data?.message}`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error in Submission",
          description: `Something Went Wrong.`,
        });
      }
    }
  };

  const onError = () => {
    toast({
      variant: "destructive",
      description: "Please fill all the mandatory fields",
    });
  };

  const handleClose = () => {
    refetch();
    setOpen(false);
    clearErrors();
    reset(initialValue);
    // setData(undefined)
    setAction("add");
  };

  const handleSearchChange = (term: string) => {
    setSearchTerm(term);
    if (term == "") {
      setValue("user_id", 0);
    }
  };

  console.log({ watcher, errors, action });
  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent className="custom-scrollbar p-0">
        <FormProvider {...form}>
          <form
            key={action}
            noValidate
            className="pb-4 "
            onSubmit={handleSubmit(onSubmit, onError)}
          >
            <SheetHeader className="pt-4 bg-white p-4">
              <SheetTitle>Manual Check-In</SheetTitle>
              <Separator className=" h-[1px] rounded-full my-2" />
            </SheetHeader>
            <SheetDescription className="px-4 space-y-3 pb-4">
              <Controller
                name="hardware_key"
                rules={{ required: "Required" }}
                control={control}
                render={({
                  field: { onChange, value, onBlur },
                  fieldState: { invalid, error },
                }) => {
                  return (
                    <Select
                      onValueChange={(value) => {
                        onChange(value);
                        const selectedHardware = hardwareOptions.find(
                          (hardware) => hardware.value === value
                        );
                        if (selectedHardware) {
                          setValue(
                            "facility_id",
                            Number(selectedHardware.facility_id)
                          );
                        }
                      }}
                      defaultValue={value}
                    >
                      <SelectTrigger floatingLabel="Hardware Key" text="*">
                        <SelectValue
                          placeholder="Select key"
                          className="text-gray-400"
                        />
                      </SelectTrigger>
                      {invalid && (
                        <span className="text-destructive block !mt-[5px] text-[12px]">
                          {error?.message}
                        </span>
                      )}
                      <SelectContent>
                        {hardwareOptions.map((item, i) => (
                          <SelectItem key={i} value={item.value}>
                            {item.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  );
                }}
              />

              <Controller
                name="user_type"
                rules={{ required: "Required" }}
                control={control}
                render={({
                  field: { onChange, value, onBlur },
                  fieldState: { invalid, error },
                }) => {
                  return (
                    <Select
                      onValueChange={(value) => {
                        onChange(value);
                        // Reset user selection when type changes
                        setValue("user_id", 0);
                        // setValue('user_name', '');
                        setSearchTerm("");
                      }}
                      defaultValue={value}
                    >
                      <SelectTrigger floatingLabel="User Type" text="*">
                        <SelectValue
                          placeholder="Select user type"
                          className="text-gray-400"
                        />
                      </SelectTrigger>
                      {invalid && (
                        <span className="text-destructive block !mt-[5px] text-[12px]">
                          {error?.message}
                        </span>
                      )}
                      <SelectContent>
                        <SelectItem value={"member"}>Member</SelectItem>
                        <SelectItem value={"coach"}>Coach</SelectItem>
                        <SelectItem value={"staff"}>Staff</SelectItem>
                      </SelectContent>
                    </Select>
                  );
                }}
              />

              {watcher.user_type && (
                <div>
                  <Controller
                    name="user_id"
                    control={control}
                    rules={{
                      validate: (value) => {
                        if (watcher.user_type && value === 0) {
                          return "Required";
                        }
                        return true;
                      },
                    }}
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <>
                        <AutocompleteCombobox<UserType>
                          items={currentUsers as UserType[]}
                          getItemKey={(user) => user.id as number}
                          getItemLabel={(user) =>
                            `${user.first_name} ${user.last_name}`
                          }
                          renderItem={(user) => {
                            const idField = (() => {
                              if ("own_member_id" in user)
                                return user.own_member_id;
                              if ("own_coach_id" in user)
                                return user.own_coach_id;
                              if ("own_staff_id" in user)
                                return user.own_staff_id;
                              return user.id;
                            })();

                            return (
                              <div className="">
                                <div className="flex gap-1 items-center text-sm">
                                  <p>{idField}:</p>
                                  <p className="capitalize">{`${user.first_name} ${user.last_name}`}</p>
                                </div>
                                <p className="text-xs text-gray-500">{`${user.email}`}</p>
                              </div>
                            );
                          }}
                          placeholder={`Search for a ${watcher.user_type}`}
                          emptyMessage="No user found."
                          onSelect={(value) => {
                            onChange(value ? value.id : 0);
                          }}
                        />
                        {error && (
                          <span className="text-destructive block !mt-[5px] text-[12px]">
                            {error.message}
                          </span>
                        )}
                      </>
                    )}
                  />
                </div>
              )}

              <LoadingButton
                type="submit"
                className="bg-primary w-full  text-black gap-1 font-semibold"
                loading={isSubmitting}
              >
                {!isSubmitting && (
                  <i className="fa-regular fa-floppy-disk text-base px-1 "></i>
                )}
                Save
              </LoadingButton>
            </SheetDescription>
          </form>
        </FormProvider>
      </SheetContent>
    </Sheet>
  );
};

export default ManualCheckInForm;
